/**
 * Real address search — 카카오 Maps SDK
 *
 * 핵심 원칙: 카카오 키워드검색 결과는 카카오 자체 관련도 판단을 거친 것이므로
 * 우리는 추가 필터링(이름 includes 등)을 강제하지 않고, 다음만 적용:
 *   1. 부수시설 페널티 (검색어와 무관한 충전소/주차장 등은 후순위)
 *   2. 카테고리 보너스 (주거시설/관광/숙박 등 장소 검색 의도에 맞는 카테고리)
 *   3. 카테고리 다양성 (같은 카테고리 max N개)
 *   4. 카카오 응답 순서 보존 (페이지 상위 = 더 관련)
 *   5. #105: 비한국어 UI일 때 결과 name을 현지 언어로 번역 (PLACE_NAMES_MAP 기반)
 */

import { getCurrentBeLang } from '../../contexts/LanguageContext';
import { translatePlaceName } from '../../i18n/placeNames';
import { translateBatch, translateQueryToKorean } from '../deepl';
import { QUERY_ALIASES, getQueryAliases } from '../../i18n/searchAliases.frozen';

const DEBUG = true;

// QUERY_ALIASES + getQueryAliases 는 src/i18n/searchAliases.frozen.js 로 분리됨.
// 임의 수정 금지 — 회귀 테스트(__tests__/searchDictionary.test.js) + CI 가 차단함.
// 본 파일에서는 import 만 하여 사용.

/**
 * 부수 시설 키워드 — 검색어에 없는데 결과 이름에 있으면 페널티
 */
const SECONDARY_FACILITIES = [
  '충전소', '충전기', '전기차',
  '주차장', '주차',
  'atm', 'cd기',
  '편의점', '카페', '커피',
  '은행', '약국',
  '주유소', 'gs칼텍스', 'sk에너지',
  '관리사무소',
];

/**
 * 교통 허브 — 검색 의도가 가장 명확함 (사용자 요청: 지하철역/공항 최상위)
 * 예: "강" → 강남역, "당" → 당산역, "인천공항" → 인천국제공항
 */
const TRANSIT_HUBS = [
  '교통,수송 > 지하철,전철',
  '교통,수송 > 지하철',
  '교통,수송 > 기차,철도',
  '교통,수송 > 기차역',
  '교통,수송 > 공항,항만',
  '교통,수송 > 공항',
  '교통,수송 > 버스,화물터미널',
  '교통,수송 > 버스터미널',
];

/**
 * 장소 검색 의도에 부합하는 카테고리
 * 사용자가 주소를 검색할 때 보통 이런 카테고리를 원함
 */
const PLACE_CATEGORIES = [
  '부동산 > 주거시설',
  '부동산 > 사무공간',
  '여행 > 관광,명소',
  '여행 > 숙박',
  '교육,학문 > 학교',
  '교육,학문 > 대학교',
  '의료,건강 > 병원',
  '의료,건강 > 종합병원',
  '서비스,산업 > 공공기관',
  '서비스,산업 > 정부기관',
];

/**
 * 골프장 카테고리 — golfMode일 때만 최상위 우선순위로 처리
 */
const GOLF_CATEGORIES = [
  '스포츠,여가 > 골프장',
  '스포츠,레저 > 골프장',
  '스포츠,여가 > 골프연습장',
];

function isGolfCategory(category) {
  const key = getCategoryKey(category);
  return GOLF_CATEGORIES.includes(key);
}

/**
 * subsequence 매칭 — "인천공항" → "인천국제공항" ✓
 */
function isSubsequence(query, text) {
  let qi = 0;
  for (let ti = 0; ti < text.length && qi < query.length; ti++) {
    if (query[qi] === text[ti]) qi++;
  }
  return qi === query.length;
}

/**
 * 부수 시설 판단
 */
function isSecondaryFacility(name, query) {
  const n = (name || '').toLowerCase();
  const q = (query || '').toLowerCase();
  return SECONDARY_FACILITIES.some((kw) => n.includes(kw) && !q.includes(kw));
}

/**
 * 카테고리 키 (첫 2단계)
 */
function getCategoryKey(category) {
  if (!category) return 'unknown';
  return category.split(' > ').slice(0, 2).join(' > ');
}

/**
 * 장소 카테고리 보너스 여부
 */
function isPlaceCategory(category) {
  const key = getCategoryKey(category);
  return PLACE_CATEGORIES.includes(key);
}

/**
 * 교통 허브(지하철역/공항/기차역/터미널) 여부
 */
function isTransitHub(category) {
  const key = getCategoryKey(category);
  return TRANSIT_HUBS.includes(key);
}

/**
 * 관련도 점수 (낮을수록 상위)
 *
 * 우선순위 그룹 (10단위):
 *   0~9   : 이름 정확 매칭 (best)
 *   10~19 : 장소 카테고리 (주거시설/관광/숙박/병원 등) — 이름 매칭 약해도 우선
 *   20~29 : 일반 결과
 *   30~39 : 부수시설 (충전소/주차장/관리사무소 등)
 *
 * 그룹 내 세부 점수 (이름 매칭 강도):
 *  +0: 이름 정확 일치
 *  +1: 이름이 검색어로 시작
 *  +2: 이름에 검색어 포함 (단어 경계)
 *  +3: 이름에 검색어 포함
 *  +4: 이름에 검색어 순서 매칭
 *  +5: 주소에 검색어 포함
 *  +6: 주소에 검색어 순서 매칭
 *  +7: 매칭 없음 (카카오 응답이지만 텍스트로는 안 잡힘)
 *
 * + 카카오 응답 순서 미세 가중 (0.001 * rank)
 */
function relevanceScore(item, query, options = {}) {
  const q = query.toLowerCase();
  const n = (item.name || '').toLowerCase();
  const a = (item.address || '').toLowerCase();
  const isGolfMode = !!options.golfMode;

  // 베이스 매칭 점수
  let base;
  if (n === q) base = 0;
  else if (n.startsWith(q)) base = 1;
  else if (n.includes(` ${q}`)) base = 2;
  else if (n.includes(q)) base = 3;
  else if (isSubsequence(q, n)) base = 4;
  else if (a.includes(q)) base = 5;
  else if (isSubsequence(q, a)) base = 6;
  else base = 7;

  // 그룹 결정 (낮을수록 상위)
  //   0~9   : (일반) 교통 허브 / (golfMode) 골프장 카테고리 — 검색어 매칭 강도 무관 최상위
  //  10~19 : 정확 일치 + 장소 카테고리
  //  20~29 : 정확 일치 + 일반
  //  30~39 : 부분 매칭 + 장소 카테고리
  //  40~49 : 부분 매칭 + 일반
  //  50~59 : 부수시설 (충전소/주차장/관리사무소)
  let group;
  const isSec = isSecondaryFacility(item.name, query);
  const isPlace = isPlaceCategory(item.category);
  const isTransit = isTransitHub(item.category);
  const isGolf = isGolfCategory(item.category);

  if (item._source === 'admin') {
    group = -10; // ADMIN DB 등록 항목은 카카오 결과 어떤 것보다도 최상위
  } else if (isSec) {
    group = 50;
  } else if (isGolfMode && isGolf) {
    group = 0; // 골프장 검색에서는 골프장 카테고리가 매칭 강도 무관 최상위
  } else if (!isGolfMode && isTransit) {
    group = 0; // 일반 검색: 지하철역/공항 등 교통 허브 최상위
  } else if (base === 0 && isPlace) {
    group = 10;
  } else if (base === 0) {
    group = 20;
  } else if (isPlace) {
    group = 30;
  } else {
    group = 40;
  }

  return group + base + (item._kakaoRank || 0) * 0.001;
}

/**
 * 카카오 키워드 검색 — 페이지 단위
 */
function kakaoKeywordSearchPage(kakao, query, page, options) {
  return new Promise((resolve) => {
    const ps = new kakao.maps.services.Places();
    const searchOpts = { size: 15, page };
    if (options.categoryGroupCode) {
      searchOpts.category_group_code = options.categoryGroupCode;
    }
    ps.keywordSearch(query, (data, status) => {
      if (status === kakao.maps.services.Status.OK) {
        resolve(data.map((d, idx) => ({
          name: d.place_name,
          address: d.road_address_name || d.address_name,
          category: d.category_name || '',
          categoryGroupCode: d.category_group_code || '',
          _source: 'keyword',
          _kakaoRank: (page - 1) * 15 + idx, // 카카오 응답 순서
        })));
      } else {
        resolve([]);
      }
    }, searchOpts);
  });
}

/**
 * 키워드 검색 — 일반 3페이지 + 지하철역 카테고리 별도 호출
 *
 * 카카오는 짧은 검색어("강", "당")에서 지하철역을 잘 안 반환함.
 * SW8(지하철역) 카테고리 코드로 별도 호출해서 강제로 가져옴.
 */
async function kakaoKeywordSearchMulti(kakao, query, options) {
  const subwayOptions = { ...options, categoryGroupCode: 'SW8' };
  const results = await Promise.all([
    kakaoKeywordSearchPage(kakao, query, 1, options),
    kakaoKeywordSearchPage(kakao, query, 2, options),
    kakaoKeywordSearchPage(kakao, query, 3, options),
    // 지하철역 전용 호출 (사용자 옵션이 다른 카테고리 지정 안 한 경우만)
    options.categoryGroupCode ? Promise.resolve([]) : kakaoKeywordSearchPage(kakao, query, 1, subwayOptions),
    options.categoryGroupCode ? Promise.resolve([]) : kakaoKeywordSearchPage(kakao, query, 2, subwayOptions),
  ]);
  return results.flat();
}

/**
 * 카카오 주소 검색 (Geocoder API)
 *
 * building_name 이 비면 name == address 가 되어 결과 카드에 같은 텍스트가 두 번 찍힘 →
 * road_name(도로명) / region_3depth_name(법정동) 을 순차 fallback. 그래도 못 채우면
 * 결과에서 제외 (UI 일관성 보호).
 */
function kakaoAddressSearch(kakao, query) {
  return new Promise((resolve) => {
    const geocoder = new kakao.maps.services.Geocoder();
    geocoder.addressSearch(query, (data, status) => {
      if (status === kakao.maps.services.Status.OK) {
        const mapped = data.map((d, idx) => {
          const buildingName = d.road_address?.building_name;
          const roadName = d.road_address?.road_name;
          const region3 = d.road_address?.region_3depth_name || d.address?.region_3depth_name;
          const address = d.road_address ? d.road_address.address_name : d.address_name;
          const displayName = buildingName || roadName || region3 || '';
          return {
            name: displayName,
            address,
            category: '',
            categoryGroupCode: '',
            _source: 'address',
            _kakaoRank: idx,
          };
        });
        resolve(mapped.filter((m) => m.name && m.name !== m.address));
      } else {
        resolve([]);
      }
    });
  });
}

/**
 * 카테고리 다양성 강제
 * - (일반) 교통 허브(지하철역/공항/기차역/터미널): 무제한
 * - (golfMode) 골프장 카테고리: 무제한 (사용자가 "뉴" 입력 시 뉴코리아/뉴서울/뉴스프링빌 모두 표시)
 * - 장소 카테고리(주거시설/관광/숙박 등): max 5개
 * - 일반 카테고리: max 3개
 * - 부수시설(충전소/주차장 등): max 1개
 */
function enforceCategoryDiversity(results, query = '', options = {}) {
  const isGolfMode = !!options.golfMode;
  const counts = new Map();
  return results.filter((item) => {
    const cat = getCategoryKey(item.category);
    const isSec = isSecondaryFacility(item.name, query);
    const isPlace = isPlaceCategory(item.category);
    const isTransit = isTransitHub(item.category);
    const isGolf = isGolfCategory(item.category);
    let limit;
    if (isSec) limit = 1;
    else if (isGolfMode && isGolf) limit = Infinity; // 골프장 검색 시 골프장 무제한
    else if (!isGolfMode && isTransit) limit = Infinity;
    else if (isPlace) limit = 5;
    else limit = 3;
    const count = counts.get(cat) || 0;
    if (count >= limit) return false;
    counts.set(cat, count + 1);
    return true;
  });
}

/**
 * 주소 검색 메인 함수
 *
 * @param {string} query
 * @param {object} options
 * @param {string} [options.categoryGroupCode] - 카카오 카테고리 그룹 코드 (예: 'HP8' 병원)
 * @param {boolean} [options.golfMode] - 골프장 검색 모드 (골프장 카테고리 우선/무제한 + category 필드 반환)
 */
export const searchAddress = async (query, options = {}) => {
  const kakao = window.kakao;
  if (!kakao || !kakao.maps || !kakao.maps.services) {
    console.warn('[AddressSearch] 카카오맵 SDK 미로드');
    return [];
  }

  const rawQ = query.trim();
  if (!rawQ) return [];

  const isGolfMode = !!options.golfMode;

  // #18: 다국어(일/중/영 등) 입력은 카카오 한국어 DB 매칭 불가 → 한국어 변환 후 검색.
  //   1차: QUERY_ALIASES 정적 dict 매칭 (가장 빠르고 안정적, 흔한 키워드 커버)
  //   2차: DeepL 자동 번역 fallback (사전에 없는 단어)
  // 단, 다음 경우엔 DeepL 호출을 skip 해야 함 (모델 hallucinate 방지):
  //   - 한글이 포함된 입력 → 이미 한국어라 번역 불필요
  //   - 사이트 한국어 모드(`lang=ko`) → 한국 사용자가 한국어 입력하는 게 정상
  //   - 입력 길이 < 4 → "dd"·"ddd" 같은 짧은 영문은 엉뚱한 한국어로 hallucinate
  //     (실제 사례: "dd" → "아쉽네요", "ddd" → 무관한 3D 시설들. 3자 이하는 의미
  //     있는 키워드로 보기 어려워 skip — 4자 이상부터만 DeepL 변환 시도)
  const aliases = getQueryAliases(rawQ);
  let q;
  if (aliases && aliases.length > 0) {
    q = aliases[0];
    if (DEBUG) console.log(`[AddressSearch] alias 매칭: "${rawQ}" → ${JSON.stringify(aliases)}`);
  } else {
    const HANGUL_RE = /[가-힣]/;
    const skipDeepL = HANGUL_RE.test(rawQ) || getCurrentBeLang() === 'ko' || rawQ.length < 4;
    if (skipDeepL) {
      q = rawQ;
    } else {
      q = await translateQueryToKorean(rawQ);
      if (DEBUG && q !== rawQ) console.log(`[AddressSearch] DeepL 변환: "${rawQ}" → "${q}"`);
    }
  }
  q = q || rawQ;

  // 별칭이 있으면 모든 별칭을 검색 (예: "palace" → 5개 궁궐 전체 호출).
  // 없으면 변환된 한국어만 호출 (foreign 원문은 결과 0이라 호출 불필요).
  const searchTerms = aliases && aliases.length > 0 ? aliases : [q];

  // 1) 카카오 API 병렬 호출 (원 쿼리 + 별칭들)
  const kakaoCalls = [];
  searchTerms.forEach((term) => {
    kakaoCalls.push(kakaoKeywordSearchMulti(kakao, term, options));
  });
  // 주소 검색은 원 쿼리만(별칭은 대부분 관광지 키워드라 address API 무의미)
  kakaoCalls.push(kakaoAddressSearch(kakao, q));

  // 카카오와 병렬로 ADMIN DB 검색 (옵션). 호출자가 fetchAdmin(q) 비동기 함수를 주입.
  // 반환값은 [{ name, address, latitude?, longitude? }, ...]. 카카오 결과 위에 prepend.
  const adminPromise = typeof options.fetchAdmin === 'function'
    ? Promise.resolve(options.fetchAdmin(q)).catch(() => [])
    : Promise.resolve([]);

  const [allResults, adminRaw] = await Promise.all([
    Promise.all(kakaoCalls),
    adminPromise,
  ]);
  const addressResults = allResults[allResults.length - 1];
  const keywordResults = allResults.slice(0, -1).flat();
  const adminResults = (Array.isArray(adminRaw) ? adminRaw : []).map((d, idx) => ({
    name: d.name || '',
    address: d.address || '',
    category: '관리자 등록',
    categoryGroupCode: '',
    _source: 'admin',
    _kakaoRank: -1000 + idx, // 항상 카카오 결과보다 상위 정렬
    _adminId: d.id,
  })).filter((d) => d.name);

  if (DEBUG) {
    console.log(`[AddressSearch] "${q}" 카카오 응답:`, {
      keyword_count: keywordResults.length,
      address_count: addressResults.length,
      categories: [...new Set(keywordResults.map((r) => getCategoryKey(r.category)))],
      golfMode: isGolfMode,
    });
  }

  // ADMIN row 가 항상 최상위. 그 다음 카카오 keyword + address.
  const combined = [...adminResults, ...keywordResults, ...addressResults];

  // 2) 점수 부여 (필터링 없음 — 카카오 결과 신뢰)
  const scored = combined.map((item) => ({
    ...item,
    _score: relevanceScore(item, q, options),
    _isSecondary: isSecondaryFacility(item.name, q),
    _isPlace: isPlaceCategory(item.category),
    _isGolf: isGolfCategory(item.category),
  }));

  // 3) 점수순 정렬 (낮은 것 = 우선순위 높음)
  scored.sort((a, b) => {
    if (a._score !== b._score) return a._score - b._score;
    return a.name.length - b.name.length;
  });

  // 4) 카테고리 다양성 (장소 max 5, 일반 max 3, 부수시설 max 1, 골프모드 시 골프장 무제한)
  const diversified = enforceCategoryDiversity(scored, q, options);

  // 5) 이름+주소 dedup
  const seen = new Set();
  const unique = diversified.filter((item) => {
    const key = `${item.name}|${item.address}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });

  if (DEBUG) {
    console.log(`[AddressSearch] "${q}" 최종 ${unique.length}개:`,
      unique.slice(0, 10).map((r) => ({
        name: r.name,
        score: r._score.toFixed(2),
        sec: r._isSecondary,
        place: r._isPlace,
        golf: r._isGolf,
        cat: getCategoryKey(r.category),
      }))
    );
  }

  // 6) 내부 필드 제거, 최대 10개. golfMode면 category 필드도 함께 반환.
  //    #105: 원본 `name`은 한글 유지 (예약 저장/지역 제한 체크 등 기존 로직 호환),
  //          UI 표시용 `nameI18n`, `addressI18n` 필드를 따로 실어 UI는 번역본을 우선 노출.
  //    순서: 정적 PLACE_NAMES_MAP 선행 → 미번역 항목만 DeepL 배치 호출 (쿼터 절약).
  //    #18: DeepL 호출이 첫 검색에 ~500~1500ms 추가 → 사용자가 "검색 안 됨"으로 오인.
  //         → 정적 사전 결과를 onPartial 로 즉시 emit, DeepL 결과는 final return 으로 채워넣음.
  const beLang = getCurrentBeLang();
  const top = unique.slice(0, 10);

  // 이름: 정적 사전 우선 적용 (즉시 — 동기)
  const nameStatic = top.map(({ name }) => translatePlaceName(name, beLang));

  const buildResult = (nameI18nArr, addressI18nArr) => top.map(({ name, address, category }, i) => {
    const base = {
      name,
      address,
      nameI18n: nameI18nArr[i] || name,
      addressI18n: addressI18nArr[i] || address,
    };
    return isGolfMode ? { ...base, category } : base;
  });

  // #18 1차 emit — 정적 사전 + 한국어 주소 fallback. 즉시 화면 노출 가능.
  if (typeof options.onPartial === 'function') {
    const addressFallback = top.map(({ address }) => address || '');
    try { options.onPartial(buildResult(nameStatic, addressFallback)); } catch (e) { /* swallow */ }
  }

  // 정적 사전 결과를 DeepL에 위임해야 하는 경우:
  //   1) 번역 안 됨 (원문 그대로 반환)
  //   2) 부분 번역 — "롯데백화점 본점" → "Lotte Department Store 본점" 처럼 한글 잔존
  //      (translatePlaceName이 startsWith 매칭 후 접미어를 원문 그대로 이어 붙이기 때문)
  const HANGUL_RE = /[가-힣]/;
  const deeplNameIdx = [];
  nameStatic.forEach((translated, i) => {
    if (beLang === 'ko') return;
    const notTranslated = translated === top[i].name;
    const partiallyTranslated = !notTranslated && HANGUL_RE.test(translated);
    if (notTranslated || partiallyTranslated) deeplNameIdx.push(i);
  });
  const deeplNameInputs = deeplNameIdx.map((i) => top[i].name);

  // 주소: 사전이 없어 전량 DeepL 대상 (타겟이 ko면 translateBatch가 원문 반환)
  const addressInputs = top.map(({ address }) => address || '');

  const [deeplNameOutputs, addressOutputs] = await Promise.all([
    translateBatch(deeplNameInputs, beLang),
    translateBatch(addressInputs, beLang),
  ]);

  // 결과 병합
  const nameI18nFinal = nameStatic.slice();
  deeplNameIdx.forEach((origIdx, i) => {
    nameI18nFinal[origIdx] = deeplNameOutputs[i] || nameStatic[origIdx];
  });

  return buildResult(nameI18nFinal, addressOutputs);
};
